import { Box, Chip, Tabs, Tab, Fade, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import PropTypes from "prop-types";
import { componentTokens } from "@accredo/fe-shared/theme/tokens/components.js";
import { useEffect, useState } from "react";

/**
 * TabList Component
 * Reusable tab list component that handles tab rendering and state management
 *
 * @param {Array} tabs - Array of tab definitions with structure: { key, label, count?, onClick? }
 * @param {string} activeTab - Key of the currently active tab
 * @param {function} onTabChange - Callback when tab changes, receives the tab key
 * @param {string} variant - Variant of the tab list, can be "standard" or "light" (lighter tab font weight)
 * @param {object} sx - Additional MUI sx styles
 * @param {object} containerSx - Additional MUI sx styles for the container
 * @param {boolean} renderContent - Whether to render the content of the active tab
 * @param {object} contentWrapperSx - Additional MUI sx styles for the content wrapper
 */
export default function TabList({
  tabs,
  activeTab,
  onTabChange,
  sx,
  variant = "standard",
  containerSx,
  renderContent = false,
  contentWrapperSx,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [fadeIn, setFadeIn] = useState(true);

  const tokens = componentTokens.tabList;

  const activeIndex = tabs?.findIndex((tab) => tab.key === activeTab);
  const safeActiveIndex = activeIndex >= 0 ? activeIndex : 0;
  const activeTabDefinition = tabs[safeActiveIndex];

  useEffect(() => {
    setFadeIn(false);
    const timer = setTimeout(() => setFadeIn(true), 100); // Kurze Pause für Fade-Reset
    return () => clearTimeout(timer);
  }, [activeTabDefinition]);

  const handleChange = (event, newValue) => {
    const tab = tabs[newValue];
    if (tab && onTabChange) {
      onTabChange(tab.key);
    }
  };

  if (!tabs || tabs.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        maxWidth: "100%",
        ...containerSx,
      }}
    >
      <Tabs
        value={safeActiveIndex}
        onChange={handleChange}
        slotProps={{
          indicator: {
            style: { display: "none" },
          },
        }}
        sx={{
          minHeight: tokens.tabs.minHeight,
          maxWidth: "100%",
          "& .MuiTabs-flexContainer": {
            gap: tokens.tabs.gap,
          },
          ".MuiTabs-scrollButtons.Mui-disabled": {
            opacity: 0.3,
          },
          ...sx,
        }}
        allowScrollButtonsMobile={isMobile}
        variant={isMobile ? "scrollable" : "standard"}
        scrollButtons={isMobile}
      >
        {tabs.map((tab, index) => (
          <Tab
            key={tab.key || index}
            label={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: tokens.label.gap,
                  color: tokens.colors.label,
                }}
              >
                <Box component="span">{tab.label}</Box>
                {tab.count !== undefined && (
                  <Chip
                    label={tab.count}
                    size="small"
                    sx={{
                      height: "auto",
                      minHeight: tokens.badge.size,
                      minWidth: tokens.badge.size,
                      backgroundColor: tokens.badge.backgroundColor,
                      borderRadius: "9999px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      px: 0,
                      "& .MuiChip-label": {
                        fontSize: tokens.badge.fontSize,
                        fontWeight: 500,
                        lineHeight: 1,
                        padding: `${tokens.badge.paddingY} ${tokens.badge.paddingX}`,
                        color: tokens.badge.textColor,
                      },
                    }}
                  />
                )}
              </Box>
            }
            sx={{
              minHeight: tokens.tab.minHeight,
              px: tokens.tab.paddingX,
              py: 0,
              minWidth: "auto",
              textTransform: "none",
              fontSize: tokens.tab.fontSize,
              fontWeight:
                variant === "light"
                  ? tokens.tab.fontWeight.light
                  : tokens.tab.fontWeight.default,
              lineHeight: tokens.tab.lineHeight,
              position: "relative",
              color: tokens.colors.label,
              "&.Mui-selected": {
                color: tokens.colors.label,
              },
              "& .MuiTab-wrapper": {
                color: tokens.colors.label,
              },
              "&:hover": {
                backgroundColor: tokens.tab.hoverBackground,
              },
              "&::after": {
                content: '""',
                position: "absolute",
                left: "50%",
                bottom: 0,
                transform: "translateX(-50%)",
                width: `calc(100% - ${tokens.indicator.widthOffset})`,
                height: tokens.indicator.inactiveHeight,
                backgroundColor: tokens.colors.indicatorInactive,
                borderRadius: tokens.indicator.borderRadius,
                opacity: 0,
                transition:
                  "background-color 0.2s ease, height 0.2s ease, opacity 0.2s ease",
              },
              "&.Mui-selected::after": {
                height: tokens.indicator.activeHeight,
                backgroundColor: tokens.colors.indicatorActive,
                opacity: 1,
              },
              "&:hover::after": {
                opacity: 1,
              },
            }}
            disableRipple
          />
        ))}
      </Tabs>

      {renderContent && activeTabDefinition?.content && (
        <Fade in={fadeIn} timeout={300} key={activeTabDefinition?.key}>
          <Box
            sx={{
              mt: theme.spacing(3),
              width: "100%",
              ...contentWrapperSx,
            }}
          >
            {typeof activeTabDefinition.content === "function"
              ? activeTabDefinition.content(activeTabDefinition)
              : activeTabDefinition.content}
          </Box>
        </Fade>
      )}
    </Box>
  );
}

TabList.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      count: PropTypes.number,
      content: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
    })
  ).isRequired,
  variant: PropTypes.oneOf(["standard", "light"]),
  activeTab: PropTypes.string.isRequired,
  onTabChange: PropTypes.func.isRequired,
  sx: PropTypes.object,
  containerSx: PropTypes.object,
  renderContent: PropTypes.bool,
  contentWrapperSx: PropTypes.object,
};
